import React, { useState } from 'react';
import {
  MessageCircle,
  User,
  Star,
  ExternalLink,
  MoreHorizontal,
  Eye,
  Users
} from 'lucide-react';
import { formatCurrency, formatRelativeTime } from '@/utils/formatting';
import { useReactions } from '@/hooks/useReactions';
import { ReactionPicker, ReactionSummary } from '@/components/reactions/ReactionPicker';
import { VotingInterface } from '@/components/voting/VotingInterface';
import { VotingAnalytics, VotingStatsCard } from '@/components/voting/VotingAnalytics';
import { ReactionType } from '@/services/reactionsService';

interface KanbanProductCardProps {
  item: any;
  participants: any[];
  isDragDisabled: boolean;
  columnColor: string;
  isDragging?: boolean;
  cartId: string;
  currentUserId?: string;
}

export const KanbanProductCard: React.FC<KanbanProductCardProps> = ({
  item,
  participants,
  isDragDisabled,
  columnColor,
  isDragging = false,
  cartId,
  currentUserId,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showVotingDetails, setShowVotingDetails] = useState(false);
  
  const product = item.product || item;
  const addedBy = participants.find(p => 
    p.userId === item.addedByUserId || p.guestId === item.addedByGuestId
  );

  // Epic 3.2: Use new reactions and voting hook
  const {
    reactions,
    reactionSummary,
    userReaction,
    votes,
    votingAnalytics,
    userVote,
    loading,
    error,
    addReaction,
    removeReaction,
    addVote,
    removeVote,
    isConnected
  } = useReactions(cartId, item.id, currentUserId);
  
  // Legacy voting for backward compatibility
  const legacyUpvotes = item.votes?.filter((v: any) => v.vote === 'upvote').length || 0;
  const legacyDownvotes = item.votes?.filter((v: any) => v.vote === 'downvote').length || 0;
  const legacyTotalVotes = legacyUpvotes + legacyDownvotes;
  const legacySentiment = legacyTotalVotes > 0 ? (legacyUpvotes / legacyTotalVotes) * 100 : 0;
  
  // Get who interacted with this product (combine reactions and votes)
  const totalInteractions = reactions.length + votes.length;
  const interactedUsers = [
    ...reactions.map(r => participants.find(p => p.userId === r.userId || p.guestId === r.guestId)),
    ...votes.map(v => participants.find(p => p.userId === v.userId || p.guestId === v.guestId))
  ].filter((user, index, self) => user && self.findIndex(u => u?.userId === user.userId) === index);
  
  return (
    <div className={`kanban-product-card bg-white rounded-lg border shadow-sm hover:shadow-md transition-all ${
      isDragging ? 'shadow-xl scale-105' : ''
    }`}>
      {/* Card Header */}
      <div className="relative">
        {/* Product Image */}
        {product.images?.[0] && (
          <div className="relative h-40 overflow-hidden rounded-t-lg">
            <img
              src={product.images[0]}
              alt={product.title}
              className="w-full h-full object-cover transition-transform hover:scale-105"
            />
            
            {/* Overlay Indicators */}
            <div className="absolute top-2 left-2">
              <div 
                className="w-2 h-2 rounded-full" 
                style={{ backgroundColor: columnColor }}
              />
            </div>
            
            <div className="absolute top-2 right-2 flex gap-1">
              {totalInteractions > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 bg-black bg-opacity-50 rounded text-white text-xs">
                  <Users className="w-3 h-3" />
                  {totalInteractions}
                </div>
              )}
              {votingAnalytics && votingAnalytics.controversyScore > 0.6 && (
                <div className="px-2 py-1 bg-orange-500 bg-opacity-90 rounded text-white text-xs font-medium">
                  Mixed
                </div>
              )}
            </div>
            
            {/* Quick Actions Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 hover:opacity-100">
              <button
                onClick={() => setShowDetails(true)}
                className="bg-white text-gray-900 px-3 py-1 rounded-full text-sm font-medium"
              >
                <Eye className="w-4 h-4 inline mr-1" />
                View Details
              </button>
            </div>
          </div>
        )}
        
        {/* No Image State */}
        {!product.images?.[0] && (
          <div className="h-32 bg-gray-100 rounded-t-lg flex items-center justify-center">
            <div className="text-center text-gray-400">
              <div className="w-12 h-12 bg-gray-200 rounded-lg mx-auto mb-2 flex items-center justify-center">
                <span className="text-lg">📦</span>
              </div>
              <p className="text-sm">No Image</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Card Content */}
      <div className="p-3">
        {/* Product Title & Price */}
        <div className="mb-2">
          <h3 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1">
            {product.title}
          </h3>
          <div className="flex items-center justify-between">
            <div className="text-lg font-bold text-gray-900">
              {formatCurrency(item.unitPrice || product.price || 0, item.currency || 'USD')}
            </div>
            {product.rating && (
              <div className="flex items-center gap-1">
                <Star className="w-3 h-3 text-yellow-400 fill-current" />
                <span className="text-xs text-gray-600">{product.rating}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Product Info */}
        <div className="space-y-2 mb-3">
          {/* Quantity */}
          {item.quantity && item.quantity > 1 && (
            <div className="text-xs text-gray-600">
              Quantity: {item.quantity}
            </div>
          )}
          
          {/* Notes */}
          {item.notes && (
            <div className="text-xs text-gray-600 italic bg-gray-50 p-2 rounded">
              "{item.notes}"
            </div>
          )}
          
          {/* Added By */}
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <User className="w-3 h-3" />
            Added by {addedBy?.displayName || 'Someone'}
            {item.createdAt && (
              <span className="ml-1">
                • {formatRelativeTime(new Date(item.createdAt))}
              </span>
            )}
          </div>
        </div>
        
        {/* Epic 3.2: Enhanced Engagement Indicators */}
        <div className="space-y-2 mb-3">
          {/* Reaction Summary */}
          {reactionSummary && reactionSummary.totalReactions > 0 && (
            <ReactionSummary 
              reactionSummary={reactionSummary} 
              className="flex-wrap" 
            />
          )}
          
          {/* Voting Analytics */}
          {votingAnalytics && votingAnalytics.totalVotes > 0 && (
            <VotingStatsCard analytics={votingAnalytics} />
          )}
          
          {/* Legacy fallback for old voting data */}
          {!votingAnalytics && legacyTotalVotes > 0 && (
            <div>
              {/* Legacy Sentiment Bar */}
              <div className="h-1 bg-gray-200 rounded-full overflow-hidden mb-1">
                <div 
                  className="h-full bg-gradient-to-r from-green-400 to-green-500 transition-all"
                  style={{ width: `${legacySentiment}%` }}
                />
              </div>
              
              {/* Legacy Vote Summary */}
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">
                  {legacyUpvotes} love • {legacyDownvotes} don't want
                </span>
                <span className="text-gray-500">
                  {Math.round(legacySentiment)}% positive
                </span>
              </div>
            </div>
          )}
        </div>
        
        {/* Participant Avatars */}
        {interactedUsers.length > 0 && (
          <div className="flex items-center gap-1 mb-3">
            <div className="flex -space-x-1">
              {interactedUsers.slice(0, 3).map((user: any, index: number) => (
                <div
                  key={index}
                  className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border border-white flex items-center justify-center text-white text-xs font-medium"
                  title={user?.displayName || 'Anonymous'}
                >
                  {(user?.displayName || 'A').charAt(0).toUpperCase()}
                </div>
              ))}
              {interactedUsers.length > 3 && (
                <div className="w-5 h-5 rounded-full bg-gray-400 border border-white flex items-center justify-center text-white text-xs">
                  +{interactedUsers.length - 3}
                </div>
              )}
            </div>
            <span className="text-xs text-gray-500 ml-1">interacted</span>
          </div>
        )}
        
        {/* Epic 3.2: Enhanced Card Actions */}
        <div className="space-y-3">
          {/* Primary Actions: Reactions and Voting */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Reaction Picker */}
              <ReactionPicker
                onReactionSelect={async (reactionType) => {
                  if (userReaction?.reactionType === reactionType) {
                    await removeReaction();
                  } else {
                    await addReaction(reactionType);
                  }
                }}
                currentReaction={userReaction?.reactionType as ReactionType}
                disabled={loading}
              />
              
              {/* Voting Interface */}
              <VotingInterface
                onVoteSubmit={async (voteType, strength, comment, isAnonymous) => {
                  if (userVote?.voteType === voteType) {
                    await removeVote();
                  } else {
                    await addVote(voteType, strength, comment, isAnonymous);
                  }
                }}
                currentVote={userVote}
                disabled={loading}
                showStrength={true}
                allowComments={true}
                allowAnonymous={true}
              />
            </div>
            
            {/* Secondary Actions */}
            <div className="flex items-center gap-1">
              {votingAnalytics && votingAnalytics.totalVotes > 0 && (
                <button 
                  onClick={() => setShowVotingDetails(!showVotingDetails)}
                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                  title="View voting details"
                >
                  <Eye className="w-4 h-4 text-gray-400" />
                </button>
              )}
              <button className="p-1.5 rounded hover:bg-gray-100 transition-colors">
                <MessageCircle className="w-4 h-4 text-gray-400" />
              </button>
              {product.vendor_url && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(product.vendor_url, '_blank');
                  }}
                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                >
                  <ExternalLink className="w-4 h-4 text-gray-400" />
                </button>
              )}
              <button className="p-1.5 rounded hover:bg-gray-100 transition-colors">
                <MoreHorizontal className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>
          
          {/* Detailed Voting Analytics (Expandable) */}
          {showVotingDetails && votingAnalytics && (
            <VotingAnalytics 
              analytics={votingAnalytics} 
              showDetailed={true}
              className="border-t pt-3"
            />
          )}
          
          {/* Connection Status */}
          {!isConnected && (
            <div className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
              ⚠ Offline - changes will sync when reconnected
            </div>
          )}
          
          {/* Error Display */}
          {error && (
            <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
              {error}
            </div>
          )}
        </div>
      </div>
      
      {/* Drag Handle Indicator */}
      {!isDragDisabled && (
        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="grid grid-cols-2 gap-0.5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="w-1 h-1 bg-gray-400 rounded-full" />
            ))}
          </div>
        </div>
      )}
      
      {/* Product Details Modal (if needed) */}
      {showDetails && (
        <ProductDetailsModal
          product={product}
          item={item}
          participants={participants}
          cartId={cartId}
          currentUserId={currentUserId}
          onClose={() => setShowDetails(false)}
        />
      )}
    </div>
  );
};

// Enhanced Product Details Modal
interface ProductDetailsModalProps {
  product: any;
  item: any;
  participants: any[];
  cartId: string;
  currentUserId?: string;
  onClose: () => void;
}

const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
  product,
  item,
  participants,
  cartId,
  currentUserId,
  onClose,
}) => {
  // Epic 3.2: Use reactions and voting in the modal
  const {
    reactionSummary,
    userReaction,
    votingAnalytics,
    userVote,
    loading,
    error,
    addReaction,
    removeReaction,
    addVote,
    removeVote,
  } = useReactions(cartId, item.id, currentUserId);
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-96 overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">{product.title}</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              ✕
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Product Image */}
            {product.images?.[0] && (
              <div>
                <img
                  src={product.images[0]}
                  alt={product.title}
                  className="w-full h-64 object-cover rounded-lg"
                />
              </div>
            )}
            
            {/* Product Details */}
            <div className="space-y-4">
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(item.unitPrice || product.price || 0, item.currency || 'USD')}
                </div>
                {item.quantity > 1 && (
                  <div className="text-sm text-gray-600">
                    Total: {formatCurrency((item.unitPrice || product.price || 0) * item.quantity, item.currency || 'USD')}
                  </div>
                )}
              </div>
              
              {product.description && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Description</h3>
                  <p className="text-sm text-gray-600">{product.description}</p>
                </div>
              )}
              
              {item.notes && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Notes</h3>
                  <p className="text-sm text-gray-600 italic">"{item.notes}"</p>
                </div>
              )}
              
              {/* Epic 3.2: Enhanced Modal Interactions */}
              <div className="space-y-4">
                {/* Reactions and Voting Section */}
                <div className="border rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-gray-900">Group Opinion</h4>
                  
                  {/* Current reactions */}
                  {reactionSummary && reactionSummary.totalReactions > 0 && (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Reactions</p>
                      <ReactionSummary reactionSummary={reactionSummary} />
                    </div>
                  )}
                  
                  {/* Voting analytics */}
                  {votingAnalytics && votingAnalytics.totalVotes > 0 && (
                    <VotingAnalytics 
                      analytics={votingAnalytics} 
                      showDetailed={true}
                    />
                  )}
                  
                  {/* Action buttons */}
                  <div className="flex items-center gap-2 pt-2 border-t">
                    <ReactionPicker
                      onReactionSelect={async (reactionType) => {
                        if (userReaction?.reactionType === reactionType) {
                          await removeReaction();
                        } else {
                          await addReaction(reactionType);
                        }
                      }}
                      currentReaction={userReaction?.reactionType as ReactionType}
                      disabled={loading}
                    />
                    
                    <VotingInterface
                      onVoteSubmit={async (voteType, strength, comment, isAnonymous) => {
                        if (userVote?.voteType === voteType) {
                          await removeVote();
                        } else {
                          await addVote(voteType, strength, comment, isAnonymous);
                        }
                      }}
                      currentVote={userVote}
                      disabled={loading}
                      showStrength={true}
                      allowComments={true}
                      allowAnonymous={true}
                    />
                  </div>
                  
                  {error && (
                    <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
                      {error}
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                    Add to Cart
                  </button>
                  {product.vendor_url && (
                    <button 
                      onClick={() => window.open(product.vendor_url, '_blank')}
                      className="flex-1 bg-gray-100 text-gray-900 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                    >
                      View Original
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};